import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import Card from './Card';
import type { OwnerProgress, OwnerStep } from '../lib/ownerProgress';

interface OwnerProgressTrackerProps {
  progress: OwnerProgress;
  /** Renders skeleton rows instead of state icons. */
  isLoading?: boolean;
}

function StepIcon({ step, index }: { step: OwnerStep; index: number }) {
  if (step.state === 'done') {
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white">
        <CheckIcon className="h-5 w-5" aria-hidden="true" />
      </span>
    );
  }

  if (step.state === 'current') {
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-primary-600 text-sm font-semibold text-primary-600">
        {index + 1}
      </span>
    );
  }

  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-400">
      {index + 1}
    </span>
  );
}

const OwnerProgressTracker: React.FC<OwnerProgressTrackerProps> = ({
  progress,
  isLoading = false,
}) => {
  const widthPercent = (progress.completedCount / 4) * 100;

  return (
    <Card padding="lg">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Your setup</h2>
        <p className="text-sm text-gray-500">{progress.completedCount} of 4 complete</p>
      </div>
      <div className="mb-6 h-2 rounded-full bg-gray-100">
        <div
          className="h-2 rounded-full bg-primary-600"
          style={{ width: `${widthPercent}%` }}
        />
      </div>
      {isLoading ? (
        <div className="space-y-4" aria-busy="true">
          {[0, 1, 2, 3].map(index => (
            <div key={index} className="flex items-start gap-4">
              <div className="h-8 w-8 rounded-full bg-gray-100" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 rounded bg-gray-100" />
                <div className="h-3 w-56 rounded bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <ol className="space-y-4">
          {progress.steps.map((step, index) => (
            <li
              key={step.id}
              className="flex items-start gap-4"
              aria-current={step.state === 'current' ? 'step' : undefined}
            >
              <StepIcon step={step} index={index} />
              <div>
                <p
                  className={`font-semibold ${
                    step.state === 'todo' ? 'text-gray-500' : 'text-gray-900'
                  }`}
                >
                  {step.title}
                </p>
                <p className="text-sm text-gray-600">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
};

export default OwnerProgressTracker;
